import { QueryParameter, openComponentInPopup, getComponent } from "./common";
import PrintingTechnologySelector, { PrintingOnlineDemand, PrintingTechnology } from "./printing-technology-selector";
import PrintingDemand from "./printing-demand";
import QuantityEdit from "./quantity-edit";
import OrderMatrix from "./order-matrix";
import ProductCount from "./product-count";
import { PopupOptions, PopupWidth } from "./popup";
import { InquiryForm } from "./inquiry-form";
import Cart from "./cart";
import { openCalculator, getProductDetailMainElement } from "./product-detail";
import LoadingIndication from "./loading-indication";
import { logProductEvent, ProductEventType } from "./product-event";

export default class PrintingSelector {
    private static readonly demandPopupId = 'printing-demand-popup';

    private linkElement: HTMLAnchorElement;
    private positionId: string;
    private positionName: string;
    private positionImageId: string;
    private modelGuid: string;
    private hasPrice = true;
    private productId: number;
    private productGuid: string;
    private productCount: number;
    private isCalculatorActive: boolean = true;
    private isOutOfStock: boolean;
    private singleProductQuantity: number;
    private isSelecting = false;

    constructor(linkElement: HTMLAnchorElement, hasPrice: boolean, productId: number, productGuid: string, productCount: number, isCalculatorActive: boolean, isOutOfStock: boolean, singleProductQuantity: number = NaN) {
        this.linkElement = linkElement;
        this.hasPrice = hasPrice;
        this.productId = productId;
        this.productGuid = productGuid;
        this.productCount = productCount;
        this.isOutOfStock = isOutOfStock;
        this.positionId = linkElement.dataset.printingPositionId;
        this.positionName = linkElement.dataset.printingPositionName;
        this.positionImageId = linkElement.dataset.printingPositionImageId;
        this.modelGuid = linkElement.dataset.modelGuid;
        this.isCalculatorActive = isCalculatorActive;
        this.singleProductQuantity = singleProductQuantity;
    }

    /** Position card: click opens the technologies popup. */
    public init() {
        this.linkElement.addEventListener("click", (evt) => {
            evt.preventDefault();
            this.openPrintingTechnologiesPopup()
        });
    }

    /**
     * Table row (position × technology already known): click continues exactly where the popup's technology
     * click would — calculator / inquiry / cart directly, or the print-details step first when the shop
     * collects a free-text demand. The technologies popup is skipped entirely.
     */
    public initTechnologyLink() {
        this.linkElement.addEventListener("click", async (evt) => {
            evt.preventDefault();
            if (this.isSelecting) {
                return;
            }
            this.isSelecting = true;
            try {
                await this.selectRowTechnology();
            } finally {
                this.isSelecting = false;
            }
        });
    }

    private async selectRowTechnology(): Promise<void> {
        const technology: PrintingTechnology = {
            id: this.linkElement.dataset.printingTechnologyId,
            name: this.linkElement.dataset.printingTechnologyName
        };

        // Same branching as PrintingTechnologySelector.technologySelected. Quantity precedence mirrors the popup,
        // which pre-fills its quantity field from the page count (online-demand entry) before falling back to
        // the product default: a count the customer already typed must win over the server default.
        if (this.isOutOfStock || this.isCalculatorActive) {
            await this.technologySelected({ technology: technology, demandText: null, productCount: this.productCount || this.singleProductQuantity });
            return;
        }

        await this.openPrintingDemandPopup(technology);
    }

    private async openPrintingDemandPopup(technology: PrintingTechnology): Promise<void> {
        await openComponentInPopup(PrintingDemand.componentName, [
            { key: 'printing-demand-with-pos-and-tech', value: 'true' }
        ], { id: PrintingSelector.demandPopupId, width: PopupWidth.Small } as PopupOptions, true);

        // The popup may hold the generic load-failed message instead of the component (HttpClient error path);
        // PrintingDemand's constructor dereferences the component root, so bail out rather than throw.
        const dialog = document.querySelector(`#${PrintingSelector.demandPopupId}`) as HTMLElement;
        if (!dialog || !getComponent(dialog, PrintingDemand.componentName)) {
            return;
        }

        const quantityEditElement = dialog.querySelector('[data-component-part="quantity-edit"]') as HTMLElement;
        const quantityEdit = quantityEditElement ? new QuantityEdit(quantityEditElement) : null;
        if (quantityEdit && this.productCount) {
            quantityEdit.setValue(this.productCount);
        }

        const demand = new PrintingDemand(dialog, (demandText) => {
            this.technologySelected({
                technology: technology,
                demandText: demandText,
                productCount: quantityEdit ? quantityEdit.getValue() : NaN
            });
        });
        demand.set(this.positionName, technology.name);
    }

    async openPrintingTechnologiesPopup(): Promise<void> {
        const params: QueryParameter[] = [
            { key: 'printingposition', value: this.positionId },
        ];

        if (this.positionImageId) {
            params.push({ key: 'printingpositionimage', value: this.positionImageId });
        }

        await openComponentInPopup('PrintingTechnologies', params, { id: 'printing-technologies-popup' } as PopupOptions, true);
        const technologiesElement = document.querySelector('[data-component="PrintingTechnologies"]') as HTMLElement;
        new PrintingTechnologySelector(technologiesElement, (tech) => {
            //popup.close();
            this.technologySelected(tech);
        }, this.productCount, this.isCalculatorActive, this.isOutOfStock);
    }

    private async technologySelected(demand: PrintingOnlineDemand): Promise<void> {
        if (!this.hasPrice || this.isOutOfStock) {
            if (this.isOutOfStock) {
                this.logQuantityUnavailable();
            }
            await this.openInquiryForm(demand);
            return;
        }

        if (this.productCount || demand.productCount) {
            const productCount = new ProductCount(this.productId, this.productGuid, demand.productCount ? demand.productCount : this.productCount);

            if (this.isCalculatorActive) {
                await openCalculator([productCount], this.positionId, demand.technology.id);
            } else {
                await this.addSingleProductPrintingGroup(productCount, demand);
            }
            return;
        }

        await this.openOrderMatrix(demand);
    }

    private logQuantityUnavailable(): void {
        const modelGuid = this.modelGuid || getProductDetailMainElement()?.dataset.modelGuid || undefined;
        const variantGuid = this.productGuid || undefined;
        void logProductEvent({
            type: ProductEventType.QuantityUnavailable,
            productModelGuid: modelGuid,
            productVariantGuid: variantGuid && variantGuid !== modelGuid ? variantGuid : undefined,
        });
    }
    private async openOrderMatrix(demand: PrintingOnlineDemand): Promise<void> {
        await openComponentInPopup(OrderMatrix.componentName, [
            { key: 'position', value: this.positionId },
            { key: 'technology', value: demand.technology.id },
            { key: 'printDescription', value: encodeURIComponent(demand.demandText) }
        ], { id: 'order-matrix-popup' } as PopupOptions, true);

        new OrderMatrix(document.querySelector('#order-matrix-popup'), (counts) => {
            this.productCountSelected(counts, this.modelGuid, this.positionId, demand);
        }, null);
    }

    private async openInquiryForm(demand: PrintingOnlineDemand): Promise<void> {
        const form = new InquiryForm();
        await form.openInPopup(null, [
            { key: 'position', value: this.positionId },
            { key: 'technology', value: demand.technology.id },
            { key: 'printDescription', value: encodeURIComponent(demand.demandText) },
            { key: 'isforprinting', value: "true" }
        ]);
    }

    private async productCountSelected(counts: ProductCount[], modelGuid: string, positionId: string, demand: PrintingOnlineDemand) {
        if (this.isCalculatorActive) {
            await openCalculator(counts, positionId, demand.technology.id);
        } else {
            const cart = new Cart();
            await cart.addPrintingGroup(counts, demand.demandText, positionId, demand.technology.id);
            await cart.openCartDialog(counts);
        }
    }

    private async addSingleProductPrintingGroup(count: ProductCount, demand: PrintingOnlineDemand) {
        this.showLoadingIndication();
        const cart = new Cart();
        await cart.addSingleProductPrintingGroup(count, demand.demandText, this.positionId, demand.technology.id);
        this.hideLoadingIndication();
        await cart.openCartDialog([count]);
    }

    // Popup flow: overlay the technologies popup. Table flow: overlay the print-details popup when open,
    // otherwise the table the row belongs to.
    private getLoadingIndicationElement(): HTMLElement {
        return (document.querySelector('[data-component="PrintingTechnologies"]')
            || document.querySelector(`#${PrintingSelector.demandPopupId}`)
            || this.linkElement.closest('[data-component-part="position-list"]')
            || this.linkElement) as HTMLElement;
    }

    private showLoadingIndication(): void {
        LoadingIndication.show(this.getLoadingIndicationElement());
    }

    private hideLoadingIndication(): void {
        LoadingIndication.hide(this.getLoadingIndicationElement());
    }
}
